/**
 * Contenuti della guida onboarding mostrata in fondo a ogni pagina (componente PageHelp).
 *
 * Data-driven: ogni pagina ha una voce con cosa-è / scenari d'uso / collegamenti con le altre
 * pagine. Le pagine sono identificate da un `pageId` (vedi mappa pattern→id in PageHelp.tsx).
 * Testo in italiano, coerente col dominio CRA del portale.
 */

export interface PageHelpContent {
  /** Titolo umano della pagina (mostrato nell'intestazione della guida). */
  title: string;
  /** "Cos'è questa pagina": una/due frasi che spiegano lo scopo. */
  what: string;
  /** Scenari d'uso tipici (chi fa cosa, e perché). */
  scenarios: string[];
  /** Come questa pagina si collega/scambia dati con le altre. */
  links: string[];
}

export const PAGE_HELP: Record<string, PageHelpContent> = {
  dashboard: {
    title: 'Dashboard',
    what: "Punto d'ingresso del portale: panoramica sullo stato di conformità CRA del tuo portfolio (prodotti monitorati, vulnerabilità aperte, scadenze di notifica).",
    scenarios: [
      'Un analista di sicurezza apre la dashboard a inizio giornata per vedere a colpo d’occhio cosa richiede attenzione.',
      'Uno sviluppatore controlla lo stato complessivo prima di entrare nel dettaglio di un prodotto.',
    ],
    links: [
      'Da qui raggiungi le aree operative: Prodotti, Tickets (schede Per prodotto, Per CVE, Per KEV), ENISA Reports.',
      'I numeri mostrati aggregano i dati prodotti nelle altre pagine (finding di Triage, KEV attivi, report SRP).',
    ],
  },

  kev: {
    title: 'Dashboard KEV',
    what: 'Elenco delle vulnerabilità note come attivamente sfruttate (catalogo CISA “Known Exploited Vulnerabilities”) che impattano i tuoi prodotti.',
    scenarios: [
      'L’analista usa i KEV per prioritizzare la remediation: una vulnerabilità sfruttata in natura su una release in commercio è la massima urgenza.',
      'Verifica rapida “siamo esposti a un KEV?” prima di decidere una notifica o una patch d’emergenza.',
    ],
    links: [
      'Un CVE elencato qui rimanda a CVE Globale (dettaglio) e al Triage della release affetta.',
      'Il catalogo KEV è incrociato automaticamente con le CVE del portfolio (vedi CVE Globale).',
    ],
  },

  'products-list': {
    title: 'Prodotti',
    what: 'Catalogo dei prodotti software soggetti al CRA. Da qui crei e gestisci i prodotti monitorati.',
    scenarios: [
      'Uno sviluppatore con permesso di gestione registra un nuovo prodotto.',
      'Un analista cerca un prodotto esistente per ispezionarne release, SBOM e vulnerabilità.',
    ],
    links: [
      'Il clic su un prodotto apre il Dettaglio prodotto (release, mapping ID, destinatari notifiche).',
      'Le release create qui alimentano l’analisi delle vulnerabilità e quindi il Triage e la vista CVE Globale.',
    ],
  },

  'product-detail': {
    title: 'Dettaglio prodotto',
    what: 'Gestione di un singolo prodotto tramite schede: Anagrafica, Release, Mapping ID, Destinatari notifiche. La scheda attiva è salvata nell’URL (#scheda) ed è condivisibile.',
    scenarios: [
      'Creare una nuova release e (opzionale) caricare subito il suo SBOM dallo stesso form: dopo «Crea release» il form si chiude e la pagina porta alla card della nuova release.',
      'Riconoscere a colpo d’occhio le release senza forniture (nessun cliente le ha ricevute: card con colori tenui e bordo tratteggiato) e archiviare quelle non più utili; da «Archiviate» si consultano e si ripristinano.',
      'Mappare gli identificatori CPE/PURL del prodotto per migliorare il match delle vulnerabilità.',
      'Configurare i destinatari delle notifiche di nuovi finding.',
    ],
    links: [
      'L’SBOM caricato viene analizzato e genera i finding: li ritrovi in Tickets (scheda Per CVE).',
      'I destinatari configurati ricevono le notifiche del flusso “nuovi finding”.',
      'Si torna all’elenco dalla pagina Prodotti.',
    ],
  },

  triage: {
    title: 'Tickets',
    what: 'Unità di lavoro di triage (scheda Per prodotto): i ticket automatici nascono per le release con CVE non triagate e clienti in campo; con “Nuovo ticket” ne crei uno a mano su più release e gli assegni le CVE cercandole tra quelle note. La scheda Per CVE è la vista globale del portfolio, da cui apri il triage del bundle (stato VEX, motivazione, export VEX).',
    scenarios: [
      'Arriva un advisory esterno senza CVE: l’analista crea un ticket manuale sulle release coinvolte e assegna le CVE appena note.',
      'L’analista parte dalla lista globale, individua una CVE ed espande le release affette per capirne l’impatto.',
      'Apre il triage del bundle, registra la decisione e la giustificazione per il dossier CRA, ed esporta il VEX.',
      'Un analista scrive le Note di analisi su un ticket «da avviare»; un collega lo prende in carico (colonna «In carico a») e ritrova le note: restano in ogni passaggio e alla promozione finiscono sul report ENISA, etichettate con il ticket.',
      'Un ticket creato manualmente si chiude come «non impattato» solo con le «Note per il cliente» (almeno 100 parole, minimo impostabile in /admin): «Chiudi» resta disabilitato finché il contatore non arriva al minimo e la nota viene inviata via email a chi ha segnalato il ticket.',
    ],
    links: [
      'I finding provengono dagli SBOM caricati nel Dettaglio prodotto (scheda Release).',
      'Presa in carico e promozione inviano una notifica email interna ad amministratori, responsabili del prodotto e a chi segue il ticket.',
      'La scheda Per KEV rimanda qui pre-espandendo la CVE selezionata.',
      'Le decisioni di triage alimentano la reportistica verso ENISA (sezione ENISA Reports).',
      'Un ticket promosso a report ENISA (tra i Ticket chiusi) linka, nel dettaglio, la pagina del report generato (uno per release).',
    ],
  },

  'enisa-reports': {
    title: 'ENISA Reports',
    what: "Thread di notifica CRA Art.14 per release, con il countdown delle scadenze (SLA). Per ogni step l'analista inoltra la notifica a ENISA (Single Reporting Platform) e la registra qui con «Registra», aggiungendo se vuole una nota (nulla viene inviato a ENISA dalla piattaforma).",
    scenarios: [
      'Rispettare le finestre di notifica: early warning entro 24h, notifica entro 72h, relazione finale entro 14 giorni (Actively Exploited Vulnerability) oppure entro 30 giorni dalla notifica 72h (Severe Incident).',
      'Registrare ogni step come fatto, con una nota facoltativa: la registrazione ferma il timer dello step (non risulterà mai scaduto dopo), è tracciata in audit e il thread passa allo step successivo.',
      'Ritrovare un report precedente (vista «Precedenti»: thread risolti o non necessari), anche cercando per prodotto, release o CVE, e aprirlo per consultarlo o correggerlo.',
    ],
    links: [
      'I report nascono dalla promozione di un Ticket, come Actively Exploited Vulnerability o Severe Incident: la categoria è nella lista e si cambia dal dettaglio.',
      'In testa alla pagina trovi il link al manuale utente ENISA della CRA SRP.',
      'Creazione di un report e ogni step registrato inviano una notifica email interna (amministratori, responsabili del prodotto, chi segue i ticket di origine).',
      'Uno step in scadenza (meno di 6h) o scaduto e non ancora registrato genera un’email automatica agli stessi destinatari, una sola volta per scadenza (job sla-evaluation).',
    ],
  },

  'enisa-report-detail': {
    title: 'Dettaglio report ENISA',
    what: 'Un thread di notifica CRA Art.14(2): categoria, avanzamento dei 3 step, CVE, note interne, rilevazione SLA e risoluzione.',
    scenarios: [
      'Registrare lo step successivo con «Registra» e una nota facoltativa: confermando, il timer dello step si ferma (in tempo o in ritardo). Correggere la rilevazione da cui partono le scadenze.',
      'Uno step registrato per errore (non inviato davvero a ENISA)? «Annulla registrazione», con motivazione: lo step torna da fare e il suo timer riparte.',
      'Un thread risolto o non necessario ferma i timer degli step mancanti; riaprirlo li fa ripartire.',
      'Le note interne raccolgono anche le Note di analisi dei ticket promossi, ciascuna sotto l’intestazione del ticket di origine: una promozione successiva le accoda, non le sovrascrive.',
      'Su un report precedente (chiuso) si possono ancora correggere note, categoria e step, oppure riaprire il thread.',
      'Ogni modifica — anche sui report precedenti — è registrata nel log audit: la sezione «Storico modifiche» mostra chi ha cambiato cosa e quando.',
      'Compilare il «Modulo ENISA» (pop-up con tutti i campi del sito ENISA, precompilati da ciò che il report sa già) e scaricarlo con «Esporta JSON» per l’estensione Chrome che compila il sito ENISA: ogni campo salvato è una voce del log audit.',
    ],
    links: [
      '«Altri report di questa release» porta ai thread precedenti o successivi della stessa release.',
      'Lo storico completo, con filtri, è nella pagina Log Audit: tipo entità «Report ENISA» e ID entità = id del report.',
    ],
  },

  admin: {
    title: 'Admin',
    what: 'Configurazione di sistema del portale: integrazioni e parametri globali (es. feed NVD/KEV, destinatari/target delle notifiche).',
    scenarios: [
      'L’amministratore configura le integrazioni esterne e i parametri di aggiornamento dati.',
      'Imposta i target delle notifiche usati dai flussi automatici.',
      'Solo in sviluppo (e da rimuovere prima della produzione): l’amministratore consulta il contenuto del file .env del server; ogni lettura è registrata nel log audit.',
      'Imposta la «Soglia Batch Edit»: un batch con un numero di modifiche (nuovi + aggiornamenti + cancellazioni) uguale o superiore resta “In attesa di conferma”; parte da solo solo sotto soglia (default 10, modifica registrata nel log audit).',
      'Imposta le parole minime della «nota per il cliente» richiesta per chiudere un ticket manuale come non impattato (default 100, da 1 a 2000, modifica registrata nel log audit).',
    ],
    links: [
      'Le impostazioni influenzano l’aggiornamento dei dati vulnerabilità (KEV/CVE Globale) e le notifiche dei flussi.',
    ],
  },
  // Slice A -- Anagrafica clienti (Forniture)
  clienti: {
    title: 'Clienti',
    what: 'Anagrafica dei clienti a cui vengono forniti i prodotti soggetti al CRA. Da qui crei e gestisci i clienti, i loro siti di installazione e i contatti per le notifiche di vulnerabilita.',
    scenarios: [
      'Un Developer registra un nuovo cliente con nome, riferimento CRM e sito web.',
      'Un analista consulta i siti e i contatti di un cliente per verificare chi riceve le notifiche di sicurezza.',
      'Il Developer aggiunge un sito (sede) o un contatto con ruolo "security" per indirizzare gli avvisi al referente corretto.',
    ],
    links: [
      'I contatti con ruolo "responsabile" o "distribuzione" ricevono le notifiche delle vulnerabilita legate ai prodotti forniti.',
      'I siti rappresentano i luoghi di installazione dei prodotti, collegati alle Forniture (scheda Forniture nel dettaglio prodotto).',
    ],
  },

  'clienti-detail': {
    title: 'Dettaglio cliente',
    what: "Scheda di un singolo cliente con tre tab: Anagrafica, Siti e Contatti. La tab attiva e' salvata nell'URL (#tab) ed e' condivisibile.",
    scenarios: [
      'Aggiungere un sito (es. sede di Milano) al cliente per tracciare i luoghi di installazione.',
      'Configurare i contatti con ruolo "security" per indirizzare gli avvisi al referente tecnico.',
      'Consultare in sola lettura la lista di contatti (utenti senza PORTFOLIO_MANAGEMENT).',
    ],
    links: [
      "Si torna all'elenco dalla pagina Clienti.",
      'I contatti configurati qui ricevono le notifiche del flusso vulnerabilita (legate ai prodotti forniti).',
      'I siti sono collegati alle forniture di prodotto: vedi la scheda Forniture nel dettaglio prodotto.',
    ],
  },

  // Slice B -- Forniture a clienti
  'product-forniture': {
    title: 'Forniture (scheda prodotto)',
    what: 'Registro delle consegne (forniture) di una release a uno o piu clienti. Ogni consegna e un evento immutabile che lega una release a un cliente (e opzionalmente a un sito). La release corrente di un cliente e la piu recente per quel gruppo (prodotto, cliente, sito).',
    scenarios: [
      'Un Developer registra una nuova consegna scegliendo il cliente, il sito opzionale, la release e la data.',
      'Un analista usa il toggle "solo correnti" per vedere quale release ha attualmente ogni cliente.',
      'Un Developer usa "Push release a clienti precedenti" per aggiornare in blocco tutti i clienti che avevano una certa release.',
      'Un analista verifica il conteggio finding di ciascuna release consegnata direttamente dalla tabella.',
    ],
    links: [
      'Il conteggio finding per riga proviene dall’analisi delle vulnerabilità della release; "-" se la release non è ancora stata analizzata.',
      'I clienti e i siti selezionabili provengono dall anagrafica Clienti (Slice A).',
      'Le release selezionabili provengono dalla scheda Release dello stesso prodotto.',
      'Il flusso automatico di notifica usa le consegne correnti per identificare i clienti da notificare.',
    ],
  },

  // Portale job Prefect
  'prefect-jobs': {
    title: 'Scheduled Job',
    what: 'Portale di monitoraggio dei flow-run Prefect: mostra le esecuzioni schedulate nel futuro e lo storico di quelle automatiche (sincronizzazione vulnerabilita, invio notifiche), con stato, orario di esecuzione previsto/effettivo, durata e log completi di ciascun job.',
    scenarios: [
      'Un analista apre la pagina per verificare se il flow di sincronizzazione vulnerabilita e terminato con successo.',
      'Un System Administrator consulta i log di un job fallito per diagnosticare il problema prima di riavviarlo.',
      'Un System Administrator usa "Esegui ora" per avviare on-demand un job schedulato senza modificarne lo schedule.',
      'Un System Administrator usa il pulsante «Avvia Scansione Vulnerabilità» (icona mano, in alto) per forzare subito la catena completa: rianalisi dei bundle, snapshot dei finding, Product Triage e notifiche; il job manual-vuln-scan compare qui con i suoi passi.',
      'Un Developer controlla la durata degli ultimi flow-run per individuare regressioni di performance.',
      'Un analista clicca il nome di un job fallito per copiarne il link (/jobs#<id-job>) e lo manda a un collega: aprendolo, la riga del job è evidenziata e i log sono già aperti.',
    ],
    links: [
      'I job elencati corrispondono ai flow Prefect definiti in prefect/flows/ (sincronizzazione KEV, notifiche clienti).',
      'Questa pagina e visibile a tutti i ruoli autenticati; il pulsante "Esegui ora" e riservato agli amministratori.',
      'Lo stato FAILED o CRASHED segnala che un flusso automatico non ha completato: verificare i log per dettagli.',
      "Ogni job ha un ID stabile (il nome del deployment, es. findings-snapshot): il link /jobs#<id> resta valido tra un'esecuzione e l'altra e vale su ogni istanza.",
    ],
  },

  // Batch Edit API
  'batch-edits': {
    title: 'Batch Edits',
    what: 'Elenco delle modifiche massive al catalogo (prodotti, release, clienti, forniture) inviate dai sistemi aziendali tramite la Batch Edit API: per ogni batch stato, righe caricate, piano (nuovi / aggiornati / cancellati), esiti di esecuzione e avanzamento, aggiornati in tempo reale.',
    scenarios: [
      'Un Developer segue l’import notturno dal gestionale: vede il batch passare da caricamento a validazione, coda ed esecuzione.',
      'Un analista verifica a colpo d’occhio se un batch è terminato con errori e quanti record ha creato, aggiornato o cancellato.',
      'Un System Administrator capisce perché un batch è “in coda”: un altro batch è in esecuzione (i batch vengono eseguiti uno alla volta).',
      'Un batch “In attesa di conferma” ha troppe modifiche per partire da solo (N ≥ soglia, sotto il badge): filtrarli per stato e aprirli per confermarli.',
    ],
    links: [
      'Il flusso API (health-check → create-batch → upload → validate-payload → run → status / run-stats) è documentato in docs/batch-edit-api.md.',
      'Click su un batch per il dettaglio: workflow delle chiamate, errori ricondotti a chunk e riga, esito per singolo record.',
      'Le modifiche applicate sono visibili nelle pagine Prodotti e Clienti.',
    ],
  },

  'batch-edit-detail': {
    title: 'Dettaglio batch',
    what: 'Esecuzione di un singolo batch: workflow delle chiamate API, statistiche per tipo di riga ed entità, timeline e chunk caricati, e l’esito di ogni record.',
    scenarios: [
      'Filtrare i record per tipo di riga (nuovo / aggiornamento / cancellazione) e per esito per trovare quelli falliti e il relativo messaggio d’errore.',
      'Leggere gli errori di validazione: ognuno indica il chunk e la riga del payload da correggere.',
      'Seguire in diretta l’avanzamento di un batch in esecuzione.',
      'Scaricare con «Scarica payload RAW» la copia dei chunk così come li ha ricevuti l’API (prima della normalizzazione), per verificare cosa ha inviato il sistema sorgente.',
      'Batch “In attesa di conferma”: leggere il motivo (modifiche e soglia), controllare il piano e, con il permesso PORTFOLIO_MANAGEMENT, confermarlo con «Forza esecuzione» (rivalidato sul catalogo attuale, registrato nel log audit).',
    ],
    links: [
      '“Analisi in sospeso”: record scritto nel catalogo, sincronizzazione con il motore di analisi delle vulnerabilità da ritentare (un nuovo push la recupera).',
      '“Nessuna modifica”: il record era già allineato; “Non eseguito”: l’esecuzione si è interrotta prima di raggiungerlo.',
    ],
  },

  // Log Audit
  'audit-log': {
    title: 'Log Audit',
    what: 'Registro append-only di tutti gli eventi di sistema di tutti i servizi: chi ha fatto cosa, quando e su quale entità, con il dettaglio prima/dopo delle modifiche.',
    scenarios: [
      'Ricostruire tutta la storia di un report ENISA: tipo entità «Report ENISA», ID entità = id del report (creazione, promozioni di ticket, note, categoria, CVE, step registrati, risoluzione, campi ed export del modulo ENISA); il timer SLA del report è il tipo «Timer SLA (report ENISA)».',
      'Aprire il dettaglio di un evento con «Mostra»: campi cambiati prima → dopo (per il modulo ENISA: etichetta ENISA e percorso del campo).',
      'Filtrare per attore, azione, servizio e intervallo di date (ora locale).',
    ],
    links: [
      'Gli eventi di un report ENISA linkano il report; lo stesso storico è nella sezione «Storico modifiche» del dettaglio.',
      'Il filtro «Tipo entità» include anche i tipi presenti nel log non ancora etichettati.',
    ],
  },
};
